import React, { FC, useCallback, useMemo, useEffect } from 'react';
import { Box, Card, CardContent } from '@mui/material';
import { ChatConversaProps, Message } from './types';
import { chatStyles } from './styles';
import { useChatScroll } from './hooks/useChatScroll';
import { ChatHeader } from './components/ChatHeader';
import { ChatInput } from './components/ChatInput';
import ChatMessage from './ChatMessage';

const DEFAULT_HEIGHT = { xs: 400, sm: 450, md: 480 };
const TYPING_ANIMATION_WIDTH = 60;

const formatTime = (): string => {
  return new Date().toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
};

const createMessage = (text: string, type: 'text' | 'audio'): Message => ({
  from: 'user',
  text: text.trim(),
  time: formatTime(),
  type,
});

const getCardStyles = (isFloatingBall: boolean, customHeight?: any) => ({
  ...(isFloatingBall ? chatStyles.floatingBallChat : chatStyles.card),
  height: customHeight || DEFAULT_HEIGHT
});

export const ChatConversa: FC<ChatConversaProps> = React.memo(({
  loading,
  showControls = false,
  onMinimize,
  onClose,
  onSendMessage,
  style,
  customStyles,
  messages,
  input,
  setInput,
  isFloatingBall = false
}) => {
  const {
    messagesContainerRef,
    scrollToBottom,
    handleScroll,
    resetUserScrolled,
  } = useChatScroll(isFloatingBall);

  useEffect(() => {
    if (isFloatingBall) {
      scrollToBottom(true);
    }
  }, [messages]);

  const sendMessage = useCallback(async () => {
    if (!input.trim()) return;
    scrollToBottom(true);

    const message = createMessage(input, 'text');
    setInput('');
    resetUserScrolled();

    if (onSendMessage) {
      try {
        await onSendMessage(message);
      } catch (error) {
        console.error('Erro ao enviar mensagem:', error);
      } finally {
      }
    }
  }, [input, setInput, resetUserScrolled, onSendMessage, scrollToBottom]);

  const handleAudioTranscription = useCallback((text: string) => {
    const message = createMessage(text, 'audio');
    scrollToBottom();

    if (onSendMessage) {
      onSendMessage(message);
    }
  }, [scrollToBottom, onSendMessage]);

  const messagesList = useMemo(() => {
    return messages?.map((msg, index) => {
      const isLastLiaMessage =
        msg.from === 'lia' &&
        messages.slice(index + 1).every((m) => m.from !== 'lia');

      const isCompleted = isFloatingBall;

      return (
        <ChatMessage
          key={index}
          msg={msg}
          isLastLiaMessage={isLastLiaMessage}
          onDone={scrollToBottom}
          onWordTyped={scrollToBottom}
          isCompleted={isCompleted}
          isFloatingBall={isFloatingBall}
        />
      );
    });
  }, [messages, scrollToBottom, isFloatingBall]);

  return (
    <Box sx={style}>
      <Card sx={getCardStyles(isFloatingBall, style?.height)}>
        <ChatHeader
          showControls={showControls}
          onMinimize={onMinimize}
          onClose={onClose}
          customStyles={customStyles}
          isExpanded={!!style?.height}
        />
        <CardContent
          ref={messagesContainerRef}
          onScroll={handleScroll}
          sx={chatStyles.messagesContainer}
        >
          <Box sx={{ flexGrow: 1 }} />
          {messagesList}
          {loading && (
            <img
              alt={'Digitando'}
              src={`${process.env.PUBLIC_URL}/images/digitando.gif`}
              style={{ width: TYPING_ANIMATION_WIDTH }}
            />
          )}
        </CardContent>
        <ChatInput
          input={input}
          setInput={setInput}
          onSendMessage={sendMessage}
          onAudioTranscription={handleAudioTranscription}
          loading={loading}
        />
      </Card>
    </Box>
  );
});

ChatConversa.displayName = 'ChatConversa';
